/**
 * TemplatesTab — refusal template cards (7 seeded).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Alert, Skeleton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmptyState from '../../common/EmptyState';
import StatusBadge from '../../common/StatusBadge';
import TemplateEditDialog from './TemplateEditDialog';
import { colors } from '../../../theme/designTokens';
import { quillApi, QuillApiError, RefusalTemplate, UpdateRefusalTemplateRequest } from '../../../services/quillAdminService';

const TemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<RefusalTemplate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RefusalTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await quillApi.guardrails.getTemplates();
      setTemplates(data);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load refusal templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (patch: UpdateRefusalTemplateRequest) => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      await quillApi.guardrails.updateTemplate(editing.template_id, patch);
      setEditing(null);
      await load();
    } catch (e) {
      setSaveError(e instanceof QuillApiError ? e.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rounded" height={180} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!templates || templates.length === 0) {
    return <EmptyState icon="no_data" title="No refusal templates found" description="Expected 7 seeded templates — check the backend." action={{ label: 'Retry', onClick: load }} />;
  }

  return (
    <Box>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={2}>
        {templates.map((t) => (
          <Grid item xs={12} sm={6} md={4} key={t.template_id}>
            <Card variant="outlined" sx={{ borderRadius: '16px', height: '100%', display: 'flex', flexDirection: 'column', borderTop: `4px solid ${colors.warning.main}` }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {t.template_key}
                  </Typography>
                  <StatusBadge status={t.is_active ? 'active' : 'inactive'} size="sm" />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                    flex: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {t.body}
                </Typography>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(t)}
                  sx={{ borderColor: colors.warning.main, color: colors.warning.dark }}
                >
                  Edit
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {editing && (
        <TemplateEditDialog
          template={editing}
          open={!!editing}
          saving={saving}
          onClose={() => (saving ? null : setEditing(null))}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default TemplatesTab;
